import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import LandingPage from './pages/LandingPage';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import OAuthCallback from './pages/OAuthCallback';
import LaunchPage from './pages/LaunchPage';
import SchoolDashboard from './school/pages/SchoolDashboard';
import InternationalizationTest from './components/InternationalizationTest';
import QueueUploadDemo from './features/document/QueueUploadDemo';
import axios from './api';
import { canAccessAdminDashboard, getDefaultRouteForUser } from './utils/authRedirect';
import 'antd/dist/reset.css';
import './i18n';

const PageLoading = ({ text }) => (
  <div style={{
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100vh',
    fontSize: '16px',
    color: '#666',
    background: '#f6faef',
  }}>
    <div style={{ textAlign: 'center' }}>
      <div style={{ fontSize: '24px', marginBottom: '16px' }}>🔄</div>
      <div>{text}</div>
    </div>
  </div>
);

// 身份验证组件
const ProtectedRoute = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem('token');
      if (!token) {
        setIsAuthenticated(false);
        setIsLoading(false);
        return;
      }

      try {
        // 验证token是否有效
        await axios.get('/api/userinfo/');
        setIsAuthenticated(true);
      } catch (error) {
        // token无效，清除本地存储
        localStorage.removeItem('token');
        setIsAuthenticated(false);
      }
      setIsLoading(false);
    };

    checkAuth();
  }, []);

  if (isLoading) {
    return <PageLoading text="正在验证身份..." />;
  }

  return isAuthenticated ? children : <Navigate to="/login" replace />;
};

// 管理后台路由：仅 admin / manager / superadmin 可访问
const AdminProtectedRoute = ({ children }) => {
  const [status, setStatus] = useState('loading');
  const [redirectTo, setRedirectTo] = useState('/school');

  useEffect(() => {
    const checkAdminAccess = async () => {
      const token = localStorage.getItem('token');
      if (!token) {
        setStatus('unauthenticated');
        return;
      }

      try {
        const res = await axios.get('/api/userinfo/');
        localStorage.setItem('user_info', JSON.stringify(res.data));
        if (canAccessAdminDashboard(res.data.role)) {
          setStatus('allowed');
        } else {
          setRedirectTo(getDefaultRouteForUser(res.data));
          setStatus('denied');
        }
      } catch (error) {
        localStorage.removeItem('token');
        localStorage.removeItem('user_info');
        setStatus('unauthenticated');
      }
    };

    checkAdminAccess();
  }, []);

  if (status === 'loading') {
    return <PageLoading text="正在验证权限..." />;
  }
  if (status === 'unauthenticated') {
    return <Navigate to="/login" replace />;
  }
  if (status === 'denied') {
    return <Navigate to={redirectTo} replace />;
  }
  return children;
};

// 登录页面组件（已登录用户按角色重定向）
const LoginRoute = ({ children }) => {
  const [isChecking, setIsChecking] = useState(true);
  const [redirectTo, setRedirectTo] = useState(null);

  useEffect(() => {
    const checkToken = async () => {
      const token = localStorage.getItem('token');
      if (!token) {
        setIsChecking(false);
        return;
      }

      try {
        const res = await axios.get('/api/userinfo/');
        localStorage.setItem('user_info', JSON.stringify(res.data));
        setRedirectTo(getDefaultRouteForUser(res.data));
      } catch (error) {
        localStorage.removeItem('token');
        localStorage.removeItem('user_info');
      }
      setIsChecking(false);
    };

    checkToken();
  }, []);

  if (isChecking) {
    return <PageLoading text="正在检查登录状态..." />;
  }

  return redirectTo ? <Navigate to={redirectTo} replace /> : children;
};

function App() {
  return (
    <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/login" element={
          <LoginRoute>
            <Login />
          </LoginRoute>
        } />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/terms" element={<TermsOfService />} />
        <Route path="/oauth2/callback" element={<OAuthCallback />} />
        <Route path="/launch" element={<LaunchPage />} />
        <Route path="/school" element={
          <ProtectedRoute>
            <SchoolDashboard />
          </ProtectedRoute>
        } />
        <Route path="/school/qa" element={
          <ProtectedRoute>
            <SchoolDashboard />
          </ProtectedRoute>
        } />
        <Route path="/test/i18n" element={
          <ProtectedRoute>
            <InternationalizationTest />
          </ProtectedRoute>
        } />
        <Route path="/test/queue-upload" element={
          <ProtectedRoute>
            <QueueUploadDemo />
          </ProtectedRoute>
        } />
        <Route path="/*" element={
          <AdminProtectedRoute>
            <Dashboard />
          </AdminProtectedRoute>
        } />
      </Routes>
    </Router>
  );
}

export default App; 